import _ from 'lodash';
import { Resizable } from 're-resizable';
import { flushSync } from 'react-dom';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import shallow from 'zustand/shallow';
import { AUTO_HEIGHT_BOXES, defaultImgAspectRatio, ItemTypes, MASKS_TYPE, roundButtonWidth, getPrimaryDark } from '../../constants/constants';
import { effectiveZoom } from '../../utils/zoom';
import { useStore } from '../../store/store';
import {
  getRatioMove,
  getRotatedBoxPositionAfterResize,
  groupFrameZIndex,
  groupMinScale,
  groupScaleAnchor,
  isATableBox,
  isScalableGroupMember,
  reinitializeBoxWidthAndHeight,
  rotatedBoxDelta,
  scaledMemberPosition,
  scalesTableColumns,
  scalesWidthWithGroup,
} from '../../utils/boxes';
import { resizeHandlersPresence, resizeHandlersStyles } from '../../utils/styles';
import { isFixedImageFit } from '../../shared/boxes/imageFit';
import { LOCK_PROPORTIONS_SHAPES, LOCK_PROPORTIONS_TYPES, NO_HEIGHT_TYPES, shapeRatios, isCompoundShape, isShapeRatioLocked, mutateRescaleSubShapes } from '../Shape/shapeUtils';
import Box from './Box';
import { gridHeight, gridWidth } from '../../constants/gridConfig';
import { maxContainerHeight } from '../../utils/containers';
import { beginSnapSession, endSnapSession, flushSnap, resolveSnapResize } from '../../snapping/snapSession';

const getStyles = (left, top, rotation) => {
  const positionTransform = `translate3d(${left}px, ${top}px, 0)`;
  const rotationTransform = _.isNumber(rotation) ? ` rotate(${rotation}deg)` : '';
  const transform = positionTransform + rotationTransform;
  return {
    position: 'absolute',
    transform,
    WebkitTransform: transform,
    // IE fallback: hide the real node using CSS when dragging
    // because IE will ignore our custom "empty image" drag preview.
  }
}

const absoluteStyle = {
  position: 'absolute',
  left: 0,
  top: 0
}

const selector =
  (id) =>
    ({
      boxes,
      masks,
      updateBox,
      scaleGroupBox,
      configuration,
      currentDocument
    }) => {
      const accentColor = getPrimaryDark(currentDocument?.type === 'template');
      let box = boxes.find((b) => b.id === id);
      if (!box) {
        for (const m of masks) {
          box = m.boxes.find((b) => b.id === id);
          if (box) break;
        }
      }

      if (!box) {
        return {
          box: undefined,
          updateBox,
          scaleGroupBox,
          configuration,
          boxesInGroup: undefined,
          header: undefined,
          footer: undefined,
          accentColor
        };
      }

      const header = masks.find(({ id }) => id === MASKS_TYPE.HEADER.id);
      const footer = masks.find(({ id }) => id === MASKS_TYPE.FOOTER.id);

      let boxesInGroup;
      if (box.type === ItemTypes.GROUP_BOXES && box.boxIds) {
        boxesInGroup = [];
        for (const b of boxes) {
          if (box.boxIds.includes(b.id)) boxesInGroup.push(b);
        }
        for (const m of masks) {
          for (const b of m.boxes) {
            if (box.boxIds.includes(b.id)) boxesInGroup.push(b);
          }
        }
      }

      return {
        box,
        updateBox,
        scaleGroupBox,
        configuration,
        boxesInGroup,
        header,
        footer,
        accentColor
      }
    }

const heightFromContent = (box) => {
  // Image en contenir / remplir : hauteur propre à la box, jamais celle de
  // l'image, même si une hauteur 'auto' a été posée (cf. handleResizeStart).
  if (isFixedImageFit(box)) return false
  const autoHeight = (box.height && box.height === 'auto') || AUTO_HEIGHT_BOXES.includes(box.type)
  return !box.height || autoHeight
}

// Curseur de la poignée imposé à toute la page le temps du geste (cf.
// .box-resizing dans Utils.css).
const lockResizeCursor = (event) => {
  const handleCursor = event?.target ? window.getComputedStyle(event.target).cursor : null;
  const root = document.documentElement;
  root.style.setProperty('--box-resize-cursor', handleCursor || 'auto');
  root.classList.add('box-resizing');
};

const unlockResizeCursor = () => {
  const root = document.documentElement;
  root.classList.remove('box-resizing');
  root.style.removeProperty('--box-resize-cursor');
};

// Position écran d'un événement souris ou tactile, null si absente.
const pointerOf = (event) => {
  const point = event?.touches?.[0] ?? event?.changedTouches?.[0] ?? event;
  return Number.isFinite(point?.clientX) && Number.isFinite(point?.clientY)
    ? { x: point.clientX, y: point.clientY }
    : null;
};

const normalizeResizableSize = (value, fallback) =>
  value === null || value === undefined ? fallback : value;


const Resizer = ({ id, drawMode, columnWidth, fromViewer, isSelected }) => {
  const {
    updateBox,
    scaleGroupBox,
    box,
    configuration,
    boxesInGroup,
    header,
    footer,
    accentColor
  } = useStore(selector(id), shallow);
  const { left, top, type, height, width, rotation, ratio, content, zIndex: boxZIndex } = box;
  const zIndex = boxesInGroup ? groupFrameZIndex(boxesInGroup) : boxZIndex
  const resizableRef = useRef(null);
  const firstRenderImgUpload = useRef(true);
  // Image en contenir / remplir : la box ne suit plus les proportions de
  // l'image, elle se redimensionne librement.
  const fixedImageFit = isFixedImageFit(box);
  const [size, setSize] = useState({
    width: normalizeResizableSize(width, columnWidth),
    height: _.isNumber(height) ? height : '100%',
  });
  const initialSize = useRef({
    width,
    height: heightFromContent({ height, type }) ? undefined : height
  });
  // Taille capturée au début de chaque opération de resize (non-draw mode)
  const resizeStartSize = useRef({ width, height });
  // Aperçu d'une mise à l'échelle de groupe. Rien n'est écrit dans le store
  // pendant le glissement : ni pile d'annulation gonflée, ni diffusion collab à
  // chaque frame, ni re-rendu React de toutes les box à chaque pixel (c'est ce
  // qui rendait le geste saccadé). Les membres étant des nœuds voisins du cadre
  // dans le DOM — pas ses enfants — leur transform est posé à la main le temps
  // du geste ; le rendu React reprend la main au relâchement.
  const groupPreview = useRef(null);
  const groupPreviewFrame = useRef(null);
  // Vrai le temps d'un glissement de la poignée générique : donne à une forme
  // composée (cf. Shape.jsx/getCompoundDesignScale) de quoi afficher un
  // aperçu de mise à l'échelle en direct, sans rien écrire dans le store tant
  // qu'on n'a pas relâché (handleResizeStop s'en charge, cf.
  // mutateRescaleSubShapes).
  const [isResizing, setIsResizing] = useState(false);
  // Resize au curseur avec magnétisme (mode dessin, cf. applyPointerResize) :
  // position du curseur et du bloc au début du geste, dernière position du
  // curseur. La taille brute se recalcule toujours depuis ce départ : repartir
  // de la taille accrochée ferait coller la box à un guide une fois franchi.
  const resizeSnap = useRef(null);
  // Dernière version du calcul de taille, pour l'écouteur de scroll posé au
  // début du geste (cf. handleResizeStart).
  const resizeMoveRef = useRef(null);

  const [position, setPosition] = useState({ left, top });

  useEffect(() => {
    setPosition({ left, top });
  }, [left, top]);

  // Frame d'aperçu en attente au démontage (box supprimée, sortie du mode
  // dessin) : sans ça elle s'exécuterait sur des nœuds détachés. Idem pour un
  // resize interrompu : écouteur de scroll et guides.
  useEffect(() => () => {
    if (groupPreviewFrame.current != null) cancelAnimationFrame(groupPreviewFrame.current);
    unlockResizeCursor();
    if (resizeSnap.current) {
      window.removeEventListener('scroll', resizeSnap.current.onScroll, { capture: true });
      resizeSnap.current = null;
      endSnapSession();
    }
  }, []);

  // Resize Img Box on img load
  useLayoutEffect(() => {
    if (firstRenderImgUpload.current) {
      firstRenderImgUpload.current = false;
      return;
    }
    if (LOCK_PROPORTIONS_TYPES.includes(type) && !fixedImageFit && ratio && resizableRef?.current) {
      resizableRef.current.updateSize({ width, height: 'auto' });
    }
  }, [fixedImageFit, ratio, type, width]);

  useEffect(() => {
    setSize({
      width: normalizeResizableSize(width, columnWidth),
      height: height ?? 'auto',
    })
  }, [width, height, columnWidth]);

  const resizableSize = useMemo(() => {
    const rawWidth = normalizeResizableSize(size.width, columnWidth);
    // Même borne que le maxWidth du Resizable plus bas : la largeur stockée
    // peut dater d'avant un rétrécissement de la colonne (ou de la cellule,
    // pour une colonne découpée en lignes) et déborderait alors visuellement.
    const clampWidth =
      box.type !== ItemTypes.LINE_BREAK &&
      _.isNumber(rawWidth) &&
      _.isNumber(columnWidth);
    return {
      width: clampWidth ? Math.min(rawWidth, columnWidth) : rawWidth,
      height: heightFromContent(box) ? 'auto' : normalizeResizableSize(size.height, 'auto'),
    };
  }, [box, columnWidth, size.height, size.width]);

  const resizableDrawSize = useMemo(() => ({
    width: normalizeResizableSize(size.width, columnWidth),
    height: heightFromContent(box) ? '100%' : normalizeResizableSize(size.height, 'auto'),
  }), [box, columnWidth, size.height, size.width]);

  const handleResizeDefaultColumn = (box, args) => {
    // args[3] = delta cumulatif depuis le début du resize (re-resizable mode contrôlé)
    // args[2].clientWidth est inutilisable : en mode contrôlé, re-resizable ne met pas
    // à jour le DOM pendant le drag, donc clientWidth reste la valeur initiale.
    const delta = args[3];
    const newWidth = Math.floor(resizeStartSize.current.width + delta.width);
    const newHeight = heightFromContent(box)
      ? 'auto'
      : Math.floor((resizeStartSize.current.height ?? 0) + delta.height);

    const newBox = reinitializeBoxWidthAndHeight({
      box: { ...box, width: newWidth, height: newHeight },
      columnWidth,
      onResize: true,
      header,
      footer,
    });
    const newSize = {
      width: newBox.width,
      height: newBox.height,
    };
    setSize(newSize);
  }

  // --- Aperçu d'une mise à l'échelle de groupe (DOM direct, hors store) ---

  // Nœud qui porte les largeurs de colonnes d'un tableau (variables CSS
  // --<clé>-size, cf. Table) : l'aperçu les réécrit pour que le tableau se
  // recompose en direct, comme il le fera au relâchement.
  const tableSizeNode = (element, columns) => {
    const firstVar = `--${columns[0].key}-size`;
    return Array.from(element?.querySelectorAll('div') ?? [])
      .find((node) => node.style.getPropertyValue(firstVar) !== '') ?? null;
  };

  const startGroupPreview = () => {
    groupPreview.current = {
      anchor: groupScaleAnchor({ left, top }),
      startWidth: width,
      minScale: groupMinScale(boxesInGroup),
      scale: 1,
      members: (boxesInGroup ?? []).map((member) => {
        const element = document.getElementById(`box-container-${member.id}`);
        const scalable = isScalableGroupMember(member);
        const columns = scalesTableColumns(member) ? member.content.columns : null;
        // Un texte ne se met pas à l'échelle : il se recompose dans une box plus
        // ou moins large. L'aperçu écrit donc cette largeur sur l'élément qui la
        // porte — le contenu de la box, seul enfant de son nœud — pour que le
        // texte se replie en direct comme il le fera au relâchement. Un tableau,
        // lui, prend sa largeur de ses colonnes.
        const content = scalable || columns || !scalesWidthWithGroup(member)
          ? null
          : document.getElementById(`box-${member.id}`)?.firstElementChild ?? null;
        const tableNode = columns ? tableSizeNode(element, columns) : null;
        return {
          element,
          content,
          tableNode,
          columns,
          tableStyle: tableNode
            ? {
              width: tableNode.style.width,
              vars: columns.map((col) => tableNode.style.getPropertyValue(`--${col.key}-size`)),
            }
            : null,
          width: member.width,
          // Hauteur dessinée, pour placer une forme mise à l'échelle autour de
          // son centre (cf. renderGroupPreview).
          height: element?.offsetHeight ?? 0,
          contentWidthStyle: content?.style.width ?? '',
          left: member.left,
          top: member.top,
          rotation: member.rotation,
          scalable,
          transform: element?.style.transform ?? '',
        };
      }),
    };
  };

  const renderGroupPreview = () => {
    const preview = groupPreview.current;
    if (!preview) return;
    const { anchor, scale } = preview;
    // Ni le cadre ni sa taille ne sont écrits ici : son coin haut-gauche est le
    // point fixe, et tant qu'il redimensionne, re-resizable tient sa taille dans
    // son propre état. Le facteur d'échelle est lu sur SON delta (cf.
    // handleResizeDrawingColumn).
    preview.members.forEach((member) => {
      if (!member.element) return;
      const moved = scaledMemberPosition({ anchor, left: member.left, top: member.top, scale });
      const rotate = _.isNumber(member.rotation) ? ` rotate(${member.rotation}deg)` : '';
      // Seuls les types dont la taille suivra vraiment sont mis à l'échelle :
      // l'aperçu montre ce que donnera le relâchement. Une box se dessine
      // tournée autour de son centre (origine par défaut) : la mise à l'échelle
      // aussi, d'où le décalage d'une demi-croissance pour que son coin
      // haut-gauche tombe à sa nouvelle place.
      if (member.scalable) {
        const shiftX = (member.element.offsetWidth * (scale - 1)) / 2;
        const shiftY = (member.height * (scale - 1)) / 2;
        member.element.style.transform =
          `translate3d(${moved.left + shiftX}px, ${moved.top + shiftY}px, 0)${rotate} scale(${scale})`;
      } else {
        member.element.style.transform = `translate3d(${moved.left}px, ${moved.top}px, 0)${rotate}`;
      }
      if (member.content && _.isNumber(member.width)) {
        member.content.style.width = `${member.width * scale}px`;
      }
      if (member.tableNode) {
        let total = 0;
        member.columns.forEach((col) => {
          const colWidth = (col.width ?? 0) * scale;
          total += colWidth;
          member.tableNode.style.setProperty(`--${col.key}-size`, `${colWidth}`);
        });
        member.tableNode.style.width = `${total}px`;
      }
    });
  };

  const scheduleGroupPreview = () => {
    if (groupPreviewFrame.current != null) return;
    groupPreviewFrame.current = requestAnimationFrame(() => {
      groupPreviewFrame.current = null;
      renderGroupPreview();
    });
  };

  // Rend aux membres leur style d'origine : le rendu React réécrit ensuite les
  // transforms depuis les valeurs du store.
  //
  // Sauf les largeurs de colonnes d'un tableau quand l'échelle va être
  // appliquée (`keepTableSizes`) : Table ne les relit du store qu'un rendu plus
  // tard (état local columnSizeVars), alors que le cadre se remesure sur le DOM
  // dès le premier (cf. shouldMeasure). Remises à l'ancienne valeur, elles
  // recaleraient le cadre sur l'ancien tableau. Celles de l'aperçu sont déjà
  // les finales ; React les réécrit ensuite à l'identique.
  const clearGroupPreview = ({ keepTableSizes = false } = {}) => {
    if (groupPreviewFrame.current != null) {
      cancelAnimationFrame(groupPreviewFrame.current);
      groupPreviewFrame.current = null;
      // Dernière frame annulée : l'aperçu n'en est pas encore à l'échelle
      // finale, qu'on s'apprête à garder.
      if (keepTableSizes) renderGroupPreview();
    }
    const preview = groupPreview.current;
    groupPreview.current = null;
    if (!preview) return null;
    preview.members.forEach((member) => {
      if (member.content) member.content.style.width = member.contentWidthStyle;
      if (member.tableNode && !keepTableSizes) {
        member.tableNode.style.width = member.tableStyle.width;
        member.columns.forEach((col, index) => {
          member.tableNode.style.setProperty(`--${col.key}-size`, member.tableStyle.vars[index]);
        });
      }
      if (!member.element) return;
      member.element.style.transform = member.transform;
    });
    return preview;
  };

  const handleResizeDrawingColumn = (box, args) => {
    const { zoomLevel, landscape } = useStore.getState();
    const zoomFactor = effectiveZoom(zoomLevel, landscape) / 100;
    const x = args[0].movementX / zoomFactor, y = args[0].movementY / zoomFactor;
    const resizeMove = rotatedBoxDelta({ rotation: box.rotation, direction: args[1], x, y });
    if (type === ItemTypes.GROUP_BOXES) {
      const preview = groupPreview.current;
      if (!preview || !preview.startWidth) return;
      // args[3] = delta cumulé depuis le début du geste, calculé par
      // re-resizable avec le ratio déjà verrouillé : le contenu suit donc
      // exactement le cadre, sans dérive d'un cumul parallèle.
      const previewWidth = Math.max(preview.startWidth + args[3].width, gridWidth);
      preview.scale = Math.max(previewWidth / preview.startWidth, preview.minScale);
      scheduleGroupPreview();
      return;
    }
    if (resizeSnap.current) {
      const point = pointerOf(args[0]);
      if (point) resizeSnap.current.pointer = point;
      applyPointerResize(box);
      return;
    }
    const ratio = resizeRatio();
    const ratioMove = getRatioMove(resizeMove, ratio);
    const newSize = {
      width: Math.max(size.width + ratioMove.width, gridWidth),
      // Une ligne ne se redimensionne qu'en longueur : son épaisseur (une ligne
      // de 2px n'est pas remontée à `gridHeight`) n'est pas celle de la box, et
      // handleResizeStop ne la relit pas.
      height: heightFromContent(box)
        ? '100%'
        : type === ItemTypes.LINE
          ? size.height
          : Math.max(((_.isNumber(size.height) ? size.height : resizeStartSize.current.height) + ratioMove.height), gridHeight),
    }
    setSize(newSize);
    if (rotation && rotation !== 0) {
      // Position calculée pour la taille qu'on vient de poser, celle que Box
      // dessine à ce rendu : avec celle du rendu précédent, la box tournée
      // (dessinée autour de son centre) décale son extrémité fixe de la moitié
      // du dernier mouvement de souris, d'où des sauts à chaque déplacement.
      const realSizeHeight = heightFromContent(box) ? resizableRef.current.resizable.clientHeight : newSize.height;
      const newPosition = getRotatedBoxPositionAfterResize(box, newSize.width, realSizeHeight, initialSize.current.height);
      setPosition({
        left: newPosition.left,
        top: newPosition.top
      })
    }
    resizableRef.current.updateSize(newSize);
  }

  // `true` (forme composée, cf. lockAspectRatio) veut dire « garder le ratio
  // courant » : getRatioMove attend, lui, un ratio largeur/hauteur — un `true`
  // y valait 1 et la hauteur variait alors du même nombre de pixels que la
  // largeur. Le ratio est celui du début du glissement (cf. handleResizeStart).
  const resizeRatio = () => {
    const { width: startWidth, height: startHeight } = resizeStartSize.current;
    return lockAspectRatio === true
      ? (startHeight > 0 ? startWidth / startHeight : false)
      : lockAspectRatio;
  };

  // Resize d'une box non tournée en mode dessin : la taille se déduit de la
  // position du curseur par rapport au bloc, pas d'un cumul de movementX/Y.
  // Ce cumul dérive du curseur (arrondis, unités de movementX selon le zoom et
  // l'écran) et ignore le scroll : en plein geste, la page bougeait sous la
  // souris sans que le bord tiré suive. Recalculé à chaque mousemove et à
  // chaque scroll, le bord reste sous le curseur, et les guides avec.
  const applyPointerResize = (box) => {
    const snap = resizeSnap.current;
    if (!snap?.pointer || !snap.startPointer) return;
    const { zoomLevel, landscape } = useStore.getState();
    const zoomFactor = effectiveZoom(zoomLevel, landscape) / 100;
    if (!zoomFactor) return;
    const rect = snap.parentEl?.getBoundingClientRect() ?? snap.startRect;
    const x = (snap.pointer.x - snap.startPointer.x - (rect.left - snap.startRect.left)) / zoomFactor;
    const y = (snap.pointer.y - snap.startPointer.y - (rect.top - snap.startRect.top)) / zoomFactor;
    const ratio = resizeRatio();
    const ratioMove = getRatioMove(rotatedBoxDelta({ rotation: 0, direction: snap.direction, x, y }), ratio);
    const { width: startWidth, height: startHeight } = resizeStartSize.current;
    // Au pixel près sur l'axe tiré : une taille fractionnaire fait trembler le
    // contour et le canvas d'une forme d'un sous-pixel à chaque mouvement.
    // L'autre axe garde sa valeur exacte.
    const resized = (start, delta) => (delta ? Math.round(start + delta) : start);
    const rawSize = {
      width: Math.max(resized(startWidth, ratioMove.width), gridWidth),
      height: heightFromContent(box)
        ? '100%'
        : type === ItemTypes.LINE
          ? size.height
          : Math.max(resized(startHeight, ratioMove.height), gridHeight),
    };
    // Proportions verrouillées : seule la largeur s'accroche, la hauteur suit.
    const direction = String(snap.direction).toLowerCase();
    const snapped = resolveSnapResize(rawSize, {
      x: direction.includes('right'),
      y: direction.includes('bottom') && !ratio,
    });
    const newSize = {
      width: snapped.width,
      height: ratio && _.isNumber(rawSize.height)
        ? rawSize.height + (snapped.width - rawSize.width) / ratio
        : snapped.height,
    };
    // re-resizable vient de poser son contour en synchrone (flushSync), à sa
    // taille brute, sans accroche ni correction de scroll ; la box et son
    // contenu suivaient, eux, au rendu React suivant. Le navigateur affichait
    // parfois l'état intermédiaire : contour et forme décalés, d'où le
    // tremblement. Même rendu synchrone ici : la frame affichée est toujours
    // celle de la taille finale.
    flushSync(() => {
      setSize(newSize);
      resizableRef.current?.updateSize(newSize);
    });
    flushSnap();
  };
  resizeMoveRef.current = () => applyPointerResize(box);

  const handleResizeStop = useCallback(() => {
    unlockResizeCursor();
    if (resizeSnap.current) {
      window.removeEventListener('scroll', resizeSnap.current.onScroll, { capture: true });
      resizeSnap.current = null;
      endSnapSession();
    }
    if (type === ItemTypes.GROUP_BOXES) {
      // Le glissement n'a touché que le DOM : le store reçoit ici le facteur
      // final, en une seule mutation pour tout le groupe.
      const scale = groupPreview.current?.scale ?? 1;
      clearGroupPreview({ keepTableSizes: scale !== 1 });
      if (scale !== 1) scaleGroupBox(id, scale);
      setIsResizing(false);
      return;
    }
    updateBox(
      id,
      (box) => {
        const oldWidth = box.width;
        const oldHeight = box.height;
        box.width = size.width;
        box.top = position.top;
        box.left = position.left;
        if (box.type !== 'line') {
          // Image en contenir / remplir : hauteur libre, gardée en pixels.
          if (NO_HEIGHT_TYPES.includes(box.type) && !isFixedImageFit(box)) {
            box.height = "auto"
          } else {
            box.height = size.height;
          }
        }
        if (isCompoundShape(box) && _.isNumber(oldWidth) && _.isNumber(oldHeight)) {
          mutateRescaleSubShapes(box, oldWidth, oldHeight);
        }
      },
      { actionNameSuffix: 'from_handle_resize' }
    );
    setIsResizing(false);
  // clearGroupPreview ne lit que des refs : recréé à chaque rendu, il ferait
  // recalculer commonResizerProps à chaque rendu pour rien.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, position.left, position.top, size.height, size.width, type, scaleGroupBox, updateBox]);

  // Le scroll ne déclenche pas de mousemove : la taille est recalculée avec
  // la dernière position connue du curseur (cf. applyPointerResize).
  const handleResizeScroll = () => resizeMoveRef.current?.();

  const handleResizeStart = (event, direction) => {
    lockResizeCursor(event);
    let startHeight = _.isNumber(size.height) ? size.height : 0;
    // Image en contenir / remplir sans hauteur en pixels : on part de la
    // hauteur affichée, sinon la poignée du bas n'aurait aucun effet.
    if (fixedImageFit && !_.isNumber(size.height)) {
      const el = drawMode ? document.getElementById(`box-container-${id}`) : resizableRef.current?.resizable;
      startHeight = el?.clientHeight || 0;
      if (startHeight) setSize((s) => ({ ...s, height: startHeight }));
    }
    resizeStartSize.current = { width: size.width, height: startHeight };
    if (type === ItemTypes.GROUP_BOXES) startGroupPreview();
    // Mêmes guides qu'au déplacement (marges, centres, box voisines). Pas pour
    // un groupe (mis à l'échelle depuis son coin) ni une box tournée, dont
    // l'emprise n'a plus ses bords sur les poignées.
    if (drawMode && !fromViewer && type !== ItemTypes.GROUP_BOXES && !rotation) {
      const boxEl = document.getElementById(`box-container-${id}`);
      beginSnapSession({
        parentId: box.columnId || box.maskId || box.pageId,
        draggedId: id,
        draggedEl: boxEl,
      });
      const parentEl = boxEl?.parentElement ?? null;
      const startPointer = pointerOf(event);
      if (parentEl && startPointer) {
        resizeSnap.current = {
          direction,
          parentEl,
          startRect: parentEl.getBoundingClientRect(),
          startPointer,
          pointer: startPointer,
          onScroll: handleResizeScroll,
        };
        window.addEventListener('scroll', handleResizeScroll, { capture: true, passive: true });
      } else {
        endSnapSession();
      }
    }
    setIsResizing(true);
  };

  const isCompound = isCompoundShape(box);

  // Le cadre d'un groupe s'arrête là où son contenu ne peut plus rétrécir (cf.
  // groupMinScale) : sans ce plancher il continuerait sous la souris, seul.
  const groupMinWidth = useMemo(() => type === ItemTypes.GROUP_BOXES && _.isNumber(width)
    ? Math.max(width * groupMinScale(boxesInGroup), gridWidth)
    : undefined, [boxesInGroup, type, width]);

  // Une forme composée n'a pas de ratio propre à sa nature (contrairement à
  // une image ou une étoile) : `true` fait garder à re-resizable le ratio
  // courant de la box (recalculé à chaque nouveau glissement), au lieu d'une
  // valeur figée. Peu importe alors laquelle des deux poignées déclenche le
  // resize (cf. allowHandlers, qui ne garde que « largeur ») : la hauteur (et
  // les sous-formes, cf. handleResizeStop) suit proportionnellement.
  // Proportions figées à la demande sur une forme qui, sans ça, se déforme
  // librement : le ratio est celui de la box au moment où le glissement
  // commence, et non `true` — `getRatioMove` a besoin d'un nombre.
  const ratioLocked = isShapeRatioLocked(box);

  // Un groupe se met à l'échelle, il ne se déforme pas : son ratio courant est
  // verrouillé (valeur numérique, et non `true` qui vaudrait un ratio de 1 dans
  // getRatioMove, donc un groupe rendu carré).
  const lockAspectRatio = useMemo(() => type === ItemTypes.GROUP_BOXES
    ? (_.isNumber(width) && _.isNumber(height) && height > 0 ? width / height : 1)
    : isCompound
    ? true
    : LOCK_PROPORTIONS_TYPES.includes(type) && !fixedImageFit
      ? (content?.ratio ?? defaultImgAspectRatio)
      : LOCK_PROPORTIONS_SHAPES.includes(content?.shape)
        ? 1 / shapeRatios(content?.shape)
        : ratioLocked && _.isNumber(box?.height) && box.height > 0
          ? box.width / box.height
          : false,
    [box?.height, box?.width, fixedImageFit, isCompound, content?.ratio, content?.shape, ratioLocked, type, width, height]);

  const allowHandlers = useMemo(() => {
    const isLine = type === 'line';
    const isGroupBox = type === ItemTypes.GROUP_BOXES;
    const isArrow = type === 'shape' && content.shape === 'arrow';
    const allowBottom = fixedImageFit || (
      !NO_HEIGHT_TYPES.includes(type)
      && !LOCK_PROPORTIONS_TYPES.includes(type)
      && !LOCK_PROPORTIONS_SHAPES.includes(content?.shape)
      && !isCompound
      && !isLine
      && !isArrow
      && !isGroupBox
      && !ratioLocked);
    // Un groupe se tire par son bord droit, comme une box : son ratio étant
    // verrouillé (cf. lockAspectRatio), la hauteur suit, sans rien déformer.
    return { right: true, bottom: allowBottom }
  }, [fixedImageFit, isCompound, content?.shape, ratioLocked, type]);

  const commonResizerProps = useMemo(() => {
    const resizeHandlers = resizeHandlersPresence(allowHandlers);
    // Hors mode dessin la box n'est pas tournée à l'écran : la poignée non plus.
    const styles = resizeHandlersStyles(drawMode ? rotation : 0);
    const handlersStyle = {
      bottom: resizeHandlers.bottom ? styles.bottom : null,
      right: resizeHandlers.right ? styles.right : null,
    };

    return {
      ref: resizableRef,
      id: 'resizable-box',
      onResizeStop: (...args) => handleResizeStop(),
      lockAspectRatio,
      enable: isSelected && !isATableBox(type) &&
        type !== ItemTypes.LINE_BREAK &&
        !box?.from_linked_section &&
        // Même règle pour un groupe : une box venue d'une section liée n'est
        // pas modifiable ici, le groupe entier reste donc figé.
        !boxesInGroup?.some((b) => b.from_linked_section) &&
        type !== ItemTypes.LINE_BREAK && type !== ItemTypes.SPACE &&
        (drawMode || type !== ItemTypes.CHECKBOXS_VARIABLE) ? resizeHandlers : false,
      handleStyles: isSelected && !isATableBox(type) ? handlersStyle : null,
      // Repère des bandes « + » qui s'écartent de la poignée du bas
      // (cf. useResizeHandleClearance).
      handleClasses: { bottom: 'resizer-bottom-handle' },
    }
  }, [allowHandlers, boxesInGroup, box?.from_linked_section, drawMode, handleResizeStop, isSelected, lockAspectRatio, rotation, type]);

  return (
    <>
      {isATableBox(type) && drawMode && (
        <div
          id={`box-container-${box?.id ? box.id : 'new'}`}
          style={{
            ...getStyles(position.left, position.top, rotation),
            zIndex: zIndex,
          }}
        >
          <Box id={id} drawMode={drawMode} fromViewer={fromViewer} isSelected={isSelected} />
          <div
            style={{
              ...absoluteStyle,
              // outline: '2px solid ' + accentColor,
              zIndex: isSelected ? 0 : zIndex,
              width: "100%",
              height: "100%",
              pointerEvents: isSelected ? 'none' : 'auto',
            }}
          >
          </div>
        </div>
      )}

      {!drawMode && <Resizable
        onResizeStart={handleResizeStart}
        onResize={(...args) => { handleResizeDefaultColumn(box, args) }}
        maxWidth={box.type !== ItemTypes.LINE_BREAK && columnWidth}
        minWidth={roundButtonWidth}
        maxHeight={isATableBox(box.type) || box.type === ItemTypes.SUMMARY ? undefined : maxContainerHeight({ header, footer })}
        minHeight={16}
        size={resizableSize}
        style={{
          outline: isATableBox(box.type) ? undefined : '2px solid ' + accentColor,
          zIndex: isSelected ? 3 : 0,
        }}
        {...commonResizerProps}
      >
        <Box
          id={id}
          width={resizableSize.width}
          height={resizableSize.height}
          resizeStartWidth={isResizing ? resizeStartSize.current.width : undefined}
          resizeStartHeight={isResizing ? resizeStartSize.current.height : undefined}
          drawMode={drawMode}
          fromViewer={fromViewer}
          isSelected={isSelected}
        />
      </Resizable>}

      {!isATableBox(type) && drawMode &&
        <div
          id={`box-container-${box?.id ? box.id : 'new'}`}
          style={{
            ...getStyles(position.left, position.top, rotation),
            zIndex: zIndex,
          }}
        >
          {/* Largeur non bornée à la colonne, comme dans Dragger : une box posée
              librement peut être plus longue que la colonne (une ligne
              verticale l'est plus que la page n'est large). Bornée, elle
              s'arrêtait à la colonne alors que sa position, calculée pour sa
              vraie largeur, glissait de côté ; et elle bougeait à la
              (dé)sélection, Dragger la dessinant, lui, en pleine largeur. */}
          <Box
            id={id}
            width={resizableDrawSize.width}
            height={resizableSize.height}
            resizeStartWidth={isResizing ? resizeStartSize.current.width : undefined}
            resizeStartHeight={isResizing ? resizeStartSize.current.height : undefined}
            drawMode={drawMode}
            fromViewer={fromViewer}
            isSelected={isSelected}
          />
          <Resizable
            onResizeStart={handleResizeStart}
            onResize={(...args) => { handleResizeDrawingColumn(box, args) }}
            minWidth={groupMinWidth}
            {...commonResizerProps}
            size={resizableDrawSize}
            style={{
              ...absoluteStyle,
              outline: '2px solid ' + accentColor,
              // Le layer de resize doit passer au-dessus du contenu quand la box est sélectionnée,
              // mais ne doit pas bloquer les clics sur le texte (seuls les handles doivent capter les events).
              zIndex: isSelected ? zIndex + 1 : zIndex,
              pointerEvents: isSelected ? 'none' : 'auto',
            }}
          />
        </div>}
    </>
  )
};

export default Resizer;
